/**
 * Route table (ARCHITECTURE §12). Every route is pre-declared here; feature pages live at
 * fixed paths and are lazy-loaded, so feature work never needs to edit this file.
 *
 *   /login  /invite#token  (/invite/:token: old links)       public
 *   /  /projects/new  /account  /admin/{users,jobs,system}   ContentLayout (centered column)
 *   /projects/:pid[/upload|exports|jobs|models|settings/:tab] ProjectLayout (left nav)
 *   /projects/:pid/images/:iid                               annotator (full-bleed)
 */
import { lazy } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';
import { AppShell, ContentLayout } from '@/layout/AppShell';
import { ProjectLayout } from '@/layout/ProjectLayout';
import { RequireAdmin, RequireAuth } from '@/layout/RequireAuth';
import { RouteError } from '@/layout/RouteError';
import AcceptInvite from '@/pages/AcceptInvite';
import Login from '@/pages/Login';
import NotFound from '@/pages/NotFound';

const Projects = lazy(() => import('@/pages/Projects'));
const ProjectNew = lazy(() => import('@/pages/ProjectNew'));
const Account = lazy(() => import('@/pages/Account'));
const DataManager = lazy(() => import('@/pages/DataManager'));
const Upload = lazy(() => import('@/pages/Upload'));
const Exports = lazy(() => import('@/pages/Exports'));
const ProjectJobs = lazy(() => import('@/pages/Jobs'));
const Models = lazy(() => import('@/pages/Models'));
const ProjectSettings = lazy(() => import('@/pages/ProjectSettings'));
const AnnotatorPage = lazy(() => import('@/annotator/AnnotatorPage'));
const AdminUsers = lazy(() => import('@/pages/admin/Users'));
const AdminJobs = lazy(() => import('@/pages/admin/Jobs'));
const AdminSystem = lazy(() => import('@/pages/admin/System'));

const router = createBrowserRouter([
  { path: '/login', element: <Login />, errorElement: <RouteError /> },
  { path: '/invite', element: <AcceptInvite />, errorElement: <RouteError /> },
  { path: '/invite/:token', element: <AcceptInvite />, errorElement: <RouteError /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      {
        // pathless: page errors render inside the shell (top bar stays usable)
        errorElement: <RouteError />,
        children: [
          {
            element: <ContentLayout />,
            children: [
              { index: true, element: <Projects /> },
              { path: 'projects/new', element: <ProjectNew /> },
              { path: 'account', element: <Account /> },
              {
                path: 'admin',
                element: <RequireAdmin />,
                children: [
                  { index: true, element: <Navigate to="users" replace /> },
                  { path: 'users', element: <AdminUsers /> },
                  { path: 'jobs', element: <AdminJobs /> },
                  { path: 'system', element: <AdminSystem /> },
                ],
              },
              { path: '*', element: <NotFound /> },
            ],
          },
          {
            path: 'projects/:pid',
            element: <ProjectLayout />,
            children: [
              { index: true, element: <DataManager /> },
              { path: 'upload', element: <Upload /> },
              { path: 'exports', element: <Exports /> },
              { path: 'jobs', element: <ProjectJobs /> },
              { path: 'models', element: <Models /> },
              { path: 'settings', element: <Navigate to="general" replace /> },
              { path: 'settings/:tab', element: <ProjectSettings /> },
            ],
          },
          { path: 'projects/:pid/images/:iid', element: <AnnotatorPage /> },
        ],
      },
    ],
  },
]);

export default function App() {
  return <RouterProvider router={router} />;
}
